import type { Branded } from "@@/utilities/branded";
import { parsePhoneNumber } from "awesome-phonenumber";
import type { Except } from "type-fest";
import { z } from "zod";

import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import {
    translatableStringFactory,
    translation,
} from "@@/shared/translations/translation";
import type { ZodTypeOf } from "@@/shared/zod-helpers";

type PhoneNumber = Branded<string, "phone_number">;
type Phone = {
    readonly _type: "PHONE";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly verified: boolean;
    readonly phoneNumber: PhoneNumber;
    readonly categoryTags?: string[];
};

type FactoryParams = Except<Phone, "_type" | "_createdAt">;

const phoneFactory = (params: FactoryParams): Phone => {
    return {
        ...params,
        _type: "PHONE",
        _createdAt: nowIsoAndUnix(),
    };
};

const phoneNumberZodSchema = (
    z.string({
        message: translatableStringFactory({
            sv: "Ogiltigt telefonnummer",
            en: "Invalid phone number",
        }),
    }) as unknown as ZodTypeOf<PhoneNumber>
).transform<PhoneNumber>((value, context) => {
    //Todo look into this?
    if (value === defaultAnonymizedPhoneNumber) return value;

    const parsedPhoneNumber = parsePhoneNumber(value);
    if (!parsedPhoneNumber.valid) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            fatal: true,
            message: `Invalid phone number${value ? `: ${value}` : ""}`,
            params: {
                translatedMessage: translation({
                    sv: `Ogiltigt telefonnummer${value ? `: ${value}` : ""}`,
                    en: `Invalid phone number${value ? `: ${value}` : ""}`,
                }),
                error: parsedPhoneNumber.error,
            },
        });
        return z.NEVER;
    }

    return parsedPhoneNumber.number.e164 as PhoneNumber;
});

const phoneZodSchema: z.ZodSchema<Phone> = z.object({
    _type: z.literal("PHONE"),
    _createdAt: isoAndUnixZodSchema,
    phoneNumber: phoneNumberZodSchema,
    verified: z.boolean(),
    categoryTags: z.array(z.string()).optional(),
});
const defaultAnonymizedPhoneNumber = "+####" as PhoneNumber;
const anonymizePhone = (phone: Phone): Phone => {
    return {
        ...phone,
        phoneNumber: defaultAnonymizedPhoneNumber,
        verified: false,
    };
};

const phoneTags = {
    public: {
        tag: "public",
        description: "can be provided to end customers",
    },
    order: {
        tag: "order",
        description: "used for orders",
    },
};

export {
    anonymizePhone,
    defaultAnonymizedPhoneNumber,
    phoneFactory,
    phoneNumberZodSchema,
    phoneTags,
    phoneZodSchema,
};
export type { Phone, PhoneNumber };
